<template>
  <!-- Una sola tarjeta con tres momentos: pedir el correo, mandarlo, y contar
       qué pasó. El alto mínimo lo fija .af-stage para que el cambio de estado
       no dé un salto: lo que se mueve es el contenido, no la página. -->
  <div class="af-wrap">
    <transition name="af-swap" mode="out-in">

      <!-- ══ 1. PEDIR EL CORREO ══ -->
      <div v-if="state === 'form'" key="form" class="af-stage">
        <p class="af-intro">{{ $t('recover.intro') }}</p>

        <!-- Acá NO hay <form> a propósito. Con un formulario de por medio el
             navegador puede terminar enviándolo por GET —navega a esta misma
             URL, le cuelga un `?` al final, recarga la SPA entera y corta el
             request que está viajando—, y no siempre se puede impedir: un
             form.submit() disparado por código ajeno (la extensión de un gestor
             de contraseñas, por ejemplo) ni siquiera emite el evento `submit`,
             así que no hay .prevent que valga. Sin form no hay envío nativo
             posible, y el Enter lo maneja el propio campo. -->
        <div @keydown.enter.prevent="onSubmit">
          <ValidatedInput
            fieldName="email"
            type="email"
            v-model="email"
            :label="$t('recover.email')"
            :placeholder="$t('recover.enter-email')"
            :validationObject="$v.email"
            :apiError="apiError"
            @clear-error="apiError = ''"
          />

          <button type="button" class="btn-auth-gradient af-send" @click="onSubmit">
            {{ $t('recover.send-link') }}
          </button>
        </div>

        <router-link :to="{ name: 'login' }" class="af-quiet">{{ $t('recover.back-to-login') }}</router-link>
      </div>

      <!-- ══ 2. ENVIANDO ══
           El sobre se dibuja solo, en loop, mientras la API contesta. Es la
           única pieza con movimiento de la pantalla. -->
      <div v-else-if="state === 'sending'" key="sending" class="af-stage af-stage--center">
        <div class="af-mark af-mark--sending" role="status" :aria-label="$t('recover.sending')">
          <svg viewBox="0 0 64 48" fill="none" aria-hidden="true">
            <rect class="af-mark__body" x="2.5" y="2.5" width="59" height="43" rx="7" />
            <path class="af-mark__flap" d="M3 7 L32 29 L61 7" />
          </svg>
        </div>

        <span class="af-eyebrow">{{ $t('recover.sending') }}</span>
        <p class="af-lead">{{ $t('recover.sending-detail', { email: sentTo }) }}</p>
      </div>

      <!-- ══ 3. ENVIADO ══ -->
      <div v-else key="sent" class="af-stage af-stage--center">
        <div class="af-mark af-mark--done" aria-hidden="true">
          <svg viewBox="0 0 64 64" fill="none">
            <circle class="af-mark__ring" cx="32" cy="32" r="29.5" />
            <path class="af-mark__check" d="M19 33.5 L28.5 43 L46 22" />
          </svg>
        </div>

        <h2 class="af-title">{{ $t('recover.sent-title') }}</h2>
        <p class="af-lead">{{ $t('recover.sent-detail', { email: sentTo }) }}</p>
        <p class="af-note">{{ $t('recover.sent-spam') }}</p>

        <router-link :to="{ name: 'login' }" class="btn-auth-gradient af-send af-send--link">
          {{ $t('recover.back-to-login') }}
        </router-link>
        <button type="button" class="af-quiet" @click="reset">{{ $t('recover.resend') }}</button>
      </div>

    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { required, email } from 'vuelidate/lib/validators'
import ValidatedInput from '@/components/ValidatedFields/ValidatedInput.vue'

/* Lo que tarda como mínimo el estado "enviando". La API suele contestar en
   menos de eso, y sin el piso el sobre aparecía y desaparecía en un parpadeo:
   se leía como un error, no como un envío. */
const MIN_SENDING_MS = 900

export default {
  name: 'Recuperar',
  components: { ValidatedInput },

  data () {
    return {
      // form | sending | sent
      state: 'form',
      email: '',
      // El correo al que se mandó, congelado: el campo se puede editar después.
      sentTo: '',
      apiError: ''
    }
  },

  validations: {
    email: { required, email }
  },

  created () { window.scrollTo(0, 0) },

  methods: {
    /**
     * Pide el enlace de recuperación. Todo pasa acá adentro: no hay recarga, no
     * hay redirección automática. La pantalla de "enviado" se queda hasta que
     * la persona decide qué hacer, que es cuando tiene que ir a su correo.
     */
    async onSubmit (e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      this.$v.$touch()
      if (this.$v.$invalid || this.state === 'sending') return

      const destino = this.email.trim()
      this.apiError = ''
      this.sentTo = destino
      this.state = 'sending'

      const empezo = Date.now()
      try {
        await axios.post(process.env.VUE_APP_API_URL + 'recovery/password', { email: destino })
        await this.holdSending(empezo)
        this.state = 'sent'
      } catch (e) {
        await this.holdSending(empezo)
        this.apiError = this.errorMessage(e)
        this.state = 'form'
      }
    },
    /* Completa el piso de MIN_SENDING_MS. Se espera también cuando falla: un
       error que aparece antes de que el ojo registre el envío se lee como si el
       botón estuviera roto. */
    holdSending (empezo) {
      const resto = MIN_SENDING_MS - (Date.now() - empezo)
      if (resto <= 0) return Promise.resolve()
      return new Promise(resolve => setTimeout(resolve, resto))
    },
    /**
     * La API valida `email|exists:usuario,correo`, así que un correo que no está
     * en la base vuelve como 422 con el detalle en `email`. Cualquier otra cosa
     * —500, la API caída, CORS— cae en el mensaje genérico: decir "correo no
     * encontrado" cuando en realidad no hubo respuesta manda a la persona a
     * buscar el error donde no está.
     */
    errorMessage (e) {
      const data = e && e.response && e.response.data
      if (data && data.email && data.email.length) return this.$t('recover.invalid-email')
      return this.$t('recover.error-generic')
    },
    /* Volver al formulario con el correo puesto: quien toca "enviar de nuevo"
       normalmente quiere el mismo destinatario, no escribirlo otra vez. */
    reset () {
      this.state = 'form'
      this.apiError = ''
      this.$v.$reset()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./auth-flow";
</style>
